/**
 * Cloudinary video delivery helpers.
 *
 * Contentful's Cloudinary field hands us a plain delivery URL, sometimes carrying whatever
 * transformations the editor's upload preset added. Serving it as-is ships the master: full
 * resolution, original codec, audio and all. So we strip it back to the public id and rebuild it.
 */

const UPLOAD_SEGMENT = '/video/upload/'

/** A Cloudinary transformation parameter, e.g. `q_auto`, `so_0`, `dpr_2.0`, or a `$w_…` variable. */
const PARAMETER = /^([a-z]{1,3}|\$[a-z])_/
const VERSION = /^v\d+$/

export interface CloudinaryVideoParts {
  /** Delivery base, up to but not including the trailing slash of `/video/upload`. */
  base: string
  /** `v1712345678` when the source URL carried a version, otherwise an empty string. */
  version: string
  /** Public id without its file extension, e.g. `interact/kycklingar`. */
  publicId: string
}

export interface CloudinaryVideoSource {
  src: string
  /** Omitted for URLs we could not parse, so the browser sniffs the type itself. */
  type?: string
  /** Viewport this rendition is for. Omitted on the tier that has to serve everyone else. */
  media?: string
}

/**
 * Best first — the browser takes the first entry it can decode.
 *
 * HEVC leads for Safari, the only one of the three Apple hardware-decodes; for a clip that loops
 * the whole time it is on screen that counts for as much as its being smaller than H.264 (426 KB
 * against 682 KB on Cloudinary's sample). Chrome and Firefox reject `hvc1` and fall to VP9. The
 * H.264 entry keeps a bare `video/mp4`: it is the safety net, and must not be skipped over a codec
 * string something parses strictly.
 *
 * No AV1: Cloudinary transcodes on first request, and an AV1 encode is slow enough that the first
 * visitor to a new clip would wait on it.
 */
const RENDITIONS = [
  { extension: 'mp4', type: 'video/mp4; codecs=hvc1', codec: 'h265' },
  { extension: 'webm', type: 'video/webm; codecs=vp9', codec: 'vp9' },
  { extension: 'mp4', type: 'video/mp4', codec: 'h264' },
] as const

/** Default delivery width; a landing block's media column tops out near 950 CSS px. */
export const DEFAULT_VIDEO_WIDTH = 1200

/**
 * Phone rendition width, sized off the wide end of the tier so nothing in range goes soft: 1.7× on
 * a 430 px phone, still 1.1× at 640. Worth 13–29% off, depending on codec.
 */
export const MOBILE_VIDEO_WIDTH = 720

/**
 * Rides on the *wide* tier, which is why that tier is listed first. Media queries on a video's
 * `<source>` only returned to Chrome and Firefox in v120, and a browser that ignores the attribute
 * takes the first playable entry — this way that browser gets the full-size rendition instead of
 * handing a desktop the phone's file. Selection runs once at load, so rotating will not swap it.
 */
const WIDE_MEDIA = '(min-width: 641px)'

const isTransformation = (segment: string): boolean =>
  segment.split(',').every(parameter => PARAMETER.test(parameter))

/**
 * Cloudinary answers 400 to a `w_` or `h_` of 0, and `frame` rounds a side under 16 down to 0.
 * From 256 wide even a 16:1 master stays 16 high. `number` also admits NaN.
 */
const MIN_WIDTH = 256

const normalizeWidth = (width: number): number =>
  Number.isFinite(width) && width >= MIN_WIDTH ? Math.round(width) : DEFAULT_VIDEO_WIDTH

/**
 * `c_limit,w_<width>` with both sides then rounded down to a multiple of 16. Some Intel hardware VP9
 * decoders paint green bands over a frame whose height is not a multiple of 8, and a 16:9 master at
 * 1200 wide is 674 high. Cloudinary does the sums because only it knows the master's size.
 */
const frame = (width: number): string =>
  [
    `if_iw_lt_${width}`,
    '$c_iw',
    'if_else',
    `$c_${width}`,
    'if_end',
    '$k_$c_mod_16',
    '$w_$c_sub_$k',
    '$h_ih_mul_$w_div_iw',
    '$r_$h_mod_16',
    'c_fill,w_$w,h_$h_sub_$r',
  ].join('/')

/**
 * `decodeURIComponent` throws on a malformed escape and the URL comes from the CMS, so a stray `%`
 * in a filename could take a landing page down. The raw segment still works: it is the form
 * Cloudinary served us.
 */
const decodeSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

const buildUrl = (
  { base, version, publicId }: CloudinaryVideoParts,
  transformation: string,
  extension: string,
): string => [base, transformation, version, `${publicId}.${extension}`].filter(Boolean).join('/')

/**
 * Split a Cloudinary video URL into the pieces we need to rebuild it. Returns `null` for anything
 * that is not one, so callers can fall back to the original rather than build a broken URL.
 */
export const parseCloudinaryVideoUrl = (url?: string): CloudinaryVideoParts | null => {
  if (!url) return null

  const httpsUrl = url.replace(/^http:\/\//i, 'https://').split(/[?#]/)[0] as string
  const uploadIndex = httpsUrl.indexOf(UPLOAD_SEGMENT)
  if (uploadIndex === -1) return null

  const base = httpsUrl.slice(0, uploadIndex + UPLOAD_SEGMENT.length - 1)
  const segments = httpsUrl
    .slice(uploadIndex + UPLOAD_SEGMENT.length)
    .split('/')
    .filter(Boolean)

  // Everything ahead of the version is a transformation chain we are about to replace. Without a
  // version we can only go by shape, so we skip leading segments built purely of `x_y` parameters —
  // never the last one, which is always the file itself.
  const versionIndex = segments.findIndex(segment => VERSION.test(segment))
  let start = 0
  if (versionIndex !== -1) start = versionIndex + 1
  else while (start < segments.length - 1 && isTransformation(segments[start] as string)) start++

  const rest = segments.slice(start)
  if (!rest.length) return null

  return {
    base,
    version: versionIndex === -1 ? '' : (segments[versionIndex] as string),
    publicId: decodeSegment(rest.join('/')).replace(/\.[a-z0-9]+$/i, ''),
  }
}

/**
 * Whether the Cloudinary field handed us a clip rather than a still. The widget takes either, so
 * the field name says nothing — the delivery URL does: videos come from `/video/upload/`.
 */
export const isCloudinaryVideo = (url?: string): boolean => parseCloudinaryVideoUrl(url) !== null

/**
 * One `<source>` per codec per size tier, capped at `width` by `frame` (which never upscales a small
 * master), `q_auto` for the bitrate and `ac_none` to drop the audio these muted players cannot use.
 *
 * The browser reads top down and takes the first entry whose `media` fits and whose `type` it can
 * decode, so tiers go widest first and codecs best first within each.
 */
export const cloudinaryVideoSources = (
  url?: string,
  width: number = DEFAULT_VIDEO_WIDTH,
): CloudinaryVideoSource[] => {
  const parts = parseCloudinaryVideoUrl(url)
  if (!parts) return url ? [{ src: url }] : []

  const wide = normalizeWidth(width)
  const narrow = Math.min(MOBILE_VIDEO_WIDTH, wide)

  // A slot no bigger than a phone gets one tier; splitting it would spell the same width twice.
  const tiers =
    narrow < wide ? [{ width: wide, media: WIDE_MEDIA }, { width: narrow }] : [{ width: wide }]

  return tiers.flatMap(({ width: tierWidth, media }) =>
    RENDITIONS.map(({ extension, type, codec }) => ({
      src: buildUrl(
        parts,
        `${frame(tierWidth)},q_auto,ac_none,f_${extension},vc_${codec}`,
        extension,
      ),
      type,
      media,
    })),
  )
}

/**
 * The video's own first frame: painted while the file buffers, and shown in its place under
 * reduced motion. `f_auto` serves AVIF/WebP where the request allows, jpeg where it does not.
 *
 * `:image` is what keeps this a still — `f_auto` on a `/video/upload/` URL otherwise decides
 * between frame and film by file extension, which would make the `.jpg` below load-bearing.
 */
export const cloudinaryVideoPoster = (
  url?: string,
  width: number = DEFAULT_VIDEO_WIDTH,
): string => {
  const parts = parseCloudinaryVideoUrl(url)
  if (!parts) return ''

  return buildUrl(parts, `so_0/${frame(normalizeWidth(width))},q_auto,f_auto:image`, 'jpg')
}

/**
 * A still for a field that takes either: the clip's first frame, or the image as given. For the
 * places only a still works, like og:image — a scraper handed an .mp4 shows nothing.
 */
export const cloudinaryStillUrl = (url?: string): string =>
  isCloudinaryVideo(url) ? cloudinaryVideoPoster(url) : (url ?? '')
